// Math helpers for Neighbourhood Intelligence.
// All functions are pure; safe to memoize at call sites.

import { METRIC_KEYS } from './dataShape';

// Header score = data-density-weighted average of the factors' scores,
// weighted by each factor's confidence (0-100). Inputs:
//   factors: { transport:{score,confidence}, schools:{...}, ... }
export function dataDensityWeightedAvg(factors) {
  let weightedSum = 0;
  let weightSum = 0;
  for (const key of METRIC_KEYS) {
    const f = factors?.[key];
    if (!f || typeof f.score !== 'number' || !isFinite(f.score)) continue;
    const c = typeof f.confidence === 'number' ? f.confidence : 50;
    weightedSum += f.score * c;
    weightSum   += c;
  }
  if (weightSum === 0) return 0;
  return Math.round(weightedSum / weightSum);
}

// Signed % diff vs an average (e.g. city average). Used by compareCopy.
export function cityRelativePercent(score, cityAvg) {
  if (typeof score !== 'number' || typeof cityAvg !== 'number' || cityAvg === 0) {
    return { diff: 0, isBetter: true };
  }
  const diff = Math.round(((score - cityAvg) / cityAvg) * 100);
  return { diff, isBetter: diff >= 0 };
}

// Percentile of `score` against a pool of scores for the same metric.
// Returns 0-100. The pool may include the current neighbourhood itself —
// the function counts strict-less-than only, so a neighbourhood is never
// considered better than itself.
export function nationalPercentile(score, allScoresForMetric) {
  if (!Array.isArray(allScoresForMetric) || allScoresForMetric.length === 0) return 50;
  const valid = allScoresForMetric.filter(x => typeof x === 'number');
  if (valid.length === 0) return 50;
  const below = valid.filter(x => x < score).length;
  return Math.round((below / valid.length) * 100);
}

// City average for a metric. `getScore` extracts the score from a row.
export function cityAverage(neighbourhoods, city, getScore) {
  const pool = (neighbourhoods || [])
    .filter(n => n.city === city)
    .map(getScore)
    .filter(x => typeof x === 'number');
  if (pool.length === 0) return null;
  const sum = pool.reduce((a, x) => a + x, 0);
  return Math.round(sum / pool.length);
}

// Per-metric verb pairs for the directional comparison copy. Both halves
// of each pair are honest: a low percentile flips to the "worse" verb so
// the framing never reads as a backhanded compliment.
const COMPARE_VERBS = {
  schools:     { better: 'Better-schooled',         worse: 'Worse-schooled' },
  amenities:   { better: 'Better-served',           worse: 'Worse-served' },
  transport:   { better: 'Better-connected',        worse: 'Worse-connected' },
  expat:       { better: 'Larger expat community',  worse: 'Smaller expat community' },
};

// Tolerance bands. ±1% on city-relative absorbs rounding; ±1 percentile
// absorbs the same on the national pool side.
const CITY_DIFF_TOLERANCE = 1;
const PERCENTILE_MEDIAN_TOLERANCE = 1;

// Returns the two-line comparison copy + tone for one factor.
//
//   {
//     city:     { text, tone }   // tone ∈ 'pos' | 'neg' | 'neutral'
//     national: { text, tone }
//   }
//
// tone drives the dot color in PercentileCompare.
//
// Math:
//   - cityDiff > +1   → "X% better than the {city} average"      (pos)
//   - cityDiff < -1   → "X% worse than the {city} average"       (neg)
//   - |cityDiff| ≤ 1  → "On par with the {city} average"         (neutral)
//
//   - percentile === 100 → "Highest-scoring neighbourhood we've measured" (pos)
//   - percentile === 0   → "Lowest-scoring neighbourhood we've measured"  (neg)
//   - |percentile - 50| ≤ 1 → "Around the median…"                        (neutral)
//   - percentile > 50    → "{betterVerb} than {percentile}%…"             (pos)
//   - percentile < 50    → "{worseVerb} than {100 - percentile}%…"        (neg)
//
// The < 50 case inverts the percentile so the phrasing is mathematically
// honest. Percentile 30 means 70% of the pool score above you, so the
// line reads "Worse-schooled than 70% of neighbourhoods we've scored".
export function compareCopy({ metricKey, city, cityDiff, percentile }) {
  const verbs = COMPARE_VERBS[metricKey] || { better: 'Better than', worse: 'Worse than' };

  // City-relative line.
  let cityLine;
  if (cityDiff > CITY_DIFF_TOLERANCE) {
    cityLine = { text: `${Math.abs(cityDiff)}% better than the ${city} average`, tone: 'pos' };
  } else if (cityDiff < -CITY_DIFF_TOLERANCE) {
    cityLine = { text: `${Math.abs(cityDiff)}% worse than the ${city} average`,  tone: 'neg' };
  } else {
    cityLine = { text: `On par with the ${city} average`, tone: 'neutral' };
  }

  // National-percentile line.
  let nationalLine;
  if (percentile === 100) {
    nationalLine = { text: "Highest-scoring neighbourhood we've measured", tone: 'pos' };
  } else if (percentile === 0) {
    nationalLine = { text: "Lowest-scoring neighbourhood we've measured", tone: 'neg' };
  } else if (Math.abs(percentile - 50) <= PERCENTILE_MEDIAN_TOLERANCE) {
    nationalLine = { text: "Around the median of neighbourhoods we've scored", tone: 'neutral' };
  } else if (percentile > 50) {
    nationalLine = { text: `${verbs.better} than ${percentile}% of neighbourhoods we've scored`, tone: 'pos' };
  } else {
    // percentile < 50 — invert so the framing is honest.
    nationalLine = { text: `${verbs.worse} than ${100 - percentile}% of neighbourhoods we've scored`, tone: 'neg' };
  }

  return { city: cityLine, national: nationalLine };
}

// Brief's confidence-component model, kept for the data team.
//   confidence = 0.35*sourceTier + 0.30*dataDensity + 0.20*recency + 0.15*coverage
export function computeConfidence({ sourceTier = 0.6, dataDensity = 0.7, recency = 0.7, coverage = 0.8 }) {
  const raw = (0.35 * sourceTier + 0.30 * dataDensity + 0.20 * recency + 0.15 * coverage) * 100;
  return Math.round(Math.max(0, Math.min(100, raw)));
}
